import React, { Component } from 'react'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import { Card, CardHeader, CardBody, Row, Col, Table, Input, FormGroup, Label } from 'reactstrap'
import { Bar } from 'react-chartjs-2'
import { RiseLoader } from 'react-spinners'
import { store } from '../../stores/reduxStore'

const PROJECTS_QUERY = gql`
  query RepairAnalysisProjects {
    projects {
      list { id name project_companies }
    }
  }
`

const PERSON_QUERY = gql`
  query RepairAnalysisPerson($person_id: ID!) {
    person(id: $person_id) { company_ids }
  }
`

const DATA_QUERY = gql`
  query StatsRepairAnalysis($project_id: ID!) {
    stats_repair_analysis(project_id: $project_id) {
      project_id
      project_name
      avg_repair_time_days
      summary { total_welds repaired_welds total_failed_mm repair_rate_pct }
      by_inspection_type { inspection_type welds_with_repair total_failed_mm avg_failed_mm }
      top_welders { welder_name repaired_welds total_failed_mm }
    }
  }
`

class RepairAnalysis extends Component {
  constructor(props) {
    super(props)
    const params = new URLSearchParams(window.location.search)
    this.state = {
      projects: [],
      project_id: params.get('project_id') || '',
      data: null,
      loading: false,
      error: null
    }
  }

  componentDidMount() {
    const { client } = this.props
    const personId = store.getState().user.data.person_id
    Promise.all([
      client.query({ query: PROJECTS_QUERY, fetchPolicy: 'network-only' }),
      client.query({ query: PERSON_QUERY, variables: { person_id: personId }, fetchPolicy: 'network-only' })
    ]).then(([pRes, meRes]) => {
      const userCompanyIds = (meRes.data.person.company_ids || []).map(String)
      const allProjects = pRes.data.projects.list
      const projects = allProjects.filter(p => {
        if (!userCompanyIds.length) return true
        const pc = typeof p.project_companies === 'string' ? JSON.parse(p.project_companies) : (p.project_companies || [])
        return pc.some(c => userCompanyIds.includes(String(c.id)))
      })
      this.setState({ projects }, () => {
        if (this.state.project_id) this.loadData(this.state.project_id)
      })
    })
  }

  loadData(project_id) {
    this.setState({ loading: true, error: null })
    this.props.client.query({
      query: DATA_QUERY,
      variables: { project_id },
      fetchPolicy: 'network-only'
    }).then(({ data }) => {
      this.setState({ data: data.stats_repair_analysis[0] || null, loading: false })
    }).catch(e => {
      this.setState({ error: e.message, loading: false })
    })
  }

  handleProjectChange(project_id) {
    this.setState({ project_id, data: null })
    const url = project_id
      ? `${window.location.pathname}?project_id=${project_id}`
      : window.location.pathname
    window.history.replaceState(null, '', url)
    if (project_id) this.loadData(project_id)
  }

  render() {
    const { projects, project_id, data, loading, error } = this.state

    const byTypeChart = data && {
      labels: data.by_inspection_type.map(t => t.inspection_type),
      datasets: [
        { label: 'Welds with repair', data: data.by_inspection_type.map(t => t.welds_with_repair), backgroundColor: 'rgba(255,99,132,0.7)' },
        { label: 'Total failed mm', data: data.by_inspection_type.map(t => t.total_failed_mm), backgroundColor: 'rgba(255,159,64,0.7)' }
      ]
    }

    return (
      <div className="animated fadeIn">
        <Card>
          <CardHeader><strong><i className="fa fa-wrench mr-2" />Repair Analysis</strong></CardHeader>
          <CardBody style={{ position: 'relative', minHeight: '200px' }}>
            <FormGroup row className="mb-3">
              <Label sm={2}>Project</Label>
              <Col sm={4}>
                <Input type="select" value={project_id} onChange={e => this.handleProjectChange(e.target.value)}>
                  <option value="">— select project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </Col>
            </FormGroup>

            {loading && <div className="spinner-item" id="loader-bg"><RiseLoader color="#20a8d8" loading={true} /></div>}
            {error && <div className="text-danger">Error: {error}</div>}
            {!project_id && !loading && <div className="text-muted">Select a project to view data.</div>}

            {data && (
              <div>
                <Row className="mb-4">
                  {[
                    ['Total welds', data.summary.total_welds, ''],
                    ['Repaired welds', data.summary.repaired_welds, data.summary.repair_rate_pct > 5 ? 'text-danger' : 'text-warning'],
                    ['Repair rate', `${data.summary.repair_rate_pct}%`, data.summary.repair_rate_pct > 5 ? 'text-danger' : ''],
                    ['Total failed mm', data.summary.total_failed_mm.toLocaleString(), ''],
                    ['Avg repair time', `${data.avg_repair_time_days} days`, data.avg_repair_time_days > 7 ? 'text-danger' : '']
                  ].map(([label, value, cls]) => (
                    <Col md="2" key={label}>
                      <div className="text-center p-3 border rounded">
                        <div className="text-muted small">{label}</div>
                        <div className={`h4 mb-0 ${cls}`}><strong>{value}</strong></div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <Row>
                  <Col md="6">
                    <h6>Repairs by inspection type</h6>
                    <Bar data={byTypeChart} options={{
                      maintainAspectRatio: true,
                      scales: { yAxes: [{ ticks: { beginAtZero: true } }] }
                    }} />
                  </Col>
                  <Col md="6">
                    <h6>Top welders with repairs</h6>
                    <Table bordered striped size="sm">
                      <thead><tr><th>Welder</th><th>Repaired welds</th><th>Failed mm</th></tr></thead>
                      <tbody>
                        {data.top_welders.map((w, i) => (
                          <tr key={i}>
                            <td>{w.welder_name}</td>
                            <td style={{ color: w.repaired_welds > 10 ? 'red' : 'inherit' }}>{w.repaired_welds}</td>
                            <td>{w.total_failed_mm.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                <div className="mt-4 p-3 border rounded bg-light" style={{ fontSize: '12px' }}>
                  <strong>How it's calculated:</strong>
                  <ul className="mb-0 mt-1 pl-3">
                    <li><strong>Total welds</strong> — all non-removed welds in the project</li>
                    <li><strong>Repaired welds</strong> — distinct welds with at least one inspection log where <code>weld_failed &gt; 0</code></li>
                    <li><strong>Repair rate</strong> — repaired_welds / total_welds × 100</li>
                    <li><strong>Total failed mm</strong> — sum of <code>weld_failed</code> from all failed inspection logs</li>
                    <li><strong>Avg repair time</strong> — average days between a failed inspection log and the next welding/grinding log on the same weld</li>
                    <li><strong>Repairs by inspection type</strong> — grouped by inspection type (VT/MT/RT/UT etc.); welds_with_repair = distinct welds with failure for that type</li>
                    <li><strong>Top welders with repairs</strong> — welders assigned to welding/grinding logs on welds that later had failures; matched by log generation order</li>
                  </ul>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }
}

export default withApollo(RepairAnalysis)



// WEBPACK FOOTER //
// ./src/components/Report/RepairAnalysis.js